'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import React, { useEffect, useState, useCallback } from 'react';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { FiShoppingBag } from 'react-icons/fi';
import { useQuery } from '@tanstack/react-query';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCurrencies } from '@eliadmin/core/src/api/general/general-api';
import type {
  ProductDetailData,
  ProductDiscountBreakdown,
  ProductPricing,
} from '@eliadmin/core/src/api/products/types/product-types';
import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { trackEcommerce } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { AddToCartDialog } from '@eliadmin/core/src/components/dialog/lazy-dialogs';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useDialogStore,
} from '@eliadmin/core/src/components/ui/dialog';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { PRODUCT_CONFIG } from '@eliadmin/core/src/constants/product-config';
import { useCartActions } from '@eliadmin/core/src/hooks/cart/use-cart-actions';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { formatPrice, roundPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import {
  getCustomValuesMultiplied,
  hasDimensions,
  isSellable,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';

// Helper to clamp a number between min and max.
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export const ADD_TO_CART_VARIANT_WITH_VARIANT_DIALOG = 'ADD_TO_CART_VARIANT_WITH_VARIANT_DIALOG';

// Type for the variant structure from the API response
export type VariantData = {
  id: number;
  product: {
    id: number;
    availability: {
      id: number;
      name: string;
      description: string;
      sellable: boolean;
      color: string | null;
    };
    unavailability: {
      id: number;
      name: string;
      description: string;
      sellable: boolean;
      color: string | null;
    };
    unit: string;
    custom_quantity: number;
    name: string;
    code: string;
    ext_code: string | null;
    quantity: number;
    min_quantity: number;
    rating: number | null;
    short_description: string;
    image: {
      id: number;
      name: string;
      url: string;
      description: string | null;
      custom_title: string;
      metadata: any;
    };
    is_favorite: boolean;
    video: any;
    type: any;
    suppliers_prices_enabled: boolean;
    suppliers: Array<{
      supplier: {
        id: number;
        name: string;
        slug: string | null;
        description: string;
        web: string | null;
        company: string | null;
        address: string | null;
        postcode: string | null;
        city: string;
        country: string | null;
        latitude: number | null;
        longitude: number | null;
      };
      availability: any;
      unavailability: any;
      name: string | null;
      is_affiliate: boolean;
      ean: string | null;
      url: string | null;
      code: string | null;
      quantity: number;
      position: number;
      wholesale_price: number;
      used_as_source: boolean;
    }>;
    tags: Array<{
      name: string;
      color: string;
    }>;
    views: number;
    pricing?: ProductPricing;
    discounts?: ProductDiscountBreakdown;
    price?: number | null;
    price_with_vat?: number | null;
    price_before_discount?: number | null;
    price_with_vat_before_discount?: number | null;
    discount?: number;
    ask_for_price: boolean;
    attributes?: Array<{
      id: number;
      name: string;
      type: string;
      min_range?: number;
      max_range?: number;
      values?: Array<{
        id: number;
        name: string;
      }>;
    }>;
  };
};

export type AddToCartVariantWithVariantDialogProps = {
  variant: VariantData;
  quantity?: number;
};

export function AddToCartVariantWithVariantDialog({
  variant,
  quantity = 1,
}: AddToCartVariantWithVariantDialogProps) {
  const { t } = useTranslations();
  const openDialog = useDialogStore(state => state.open);
  const closeDialog = useDialogStore(state => state.close);
  const locale = useLocaleStore(s => s.currentLocale);

  const [dimensions, setDimensions] = useState<Record<string, string>>({});
  const [selectedQuantity, setSelectedQuantity] = useState(quantity);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customValues, setCustomValues] = useState<
    Array<{
      id: number;
      name: string;
      unit: string;
      value: number;
    }>
  >([]);
  const [customValuesError, setCustomValuesError] = useState(false);

  const product = variant.product || variant;

  const getServicesPrice = (vat = false) => {
    // For this simplified version, we'll return 0 as we're not handling services
    return 0;
  };

  // Get the range attributes.
  const rangeAttributes = React.useMemo(() => {
    return product?.attributes?.filter((attr: any) => attr.type === 'range') || [];
  }, [product.attributes]);

  useEffect(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, []);

  const { addToCartMutation } = useCartActions();

  // Create a simplified product object that matches the expected interface
  const simplifiedProduct = {
    ...product,
    slug: product.code || product.id.toString(),
    accessories: [],
    ratingCount: 0,
    description: product.short_description,
    attributes: product.attributes || [],
    availability: product.availability,
    unavailability: product.unavailability,
    unit: product.unit,
    custom_quantity: typeof product.custom_quantity === 'number' ? product.custom_quantity : 1,
    name: product.name,
    code: product.code,
    ext_code: product.ext_code,
    quantity: product.quantity,
    min_quantity: product.min_quantity,
    rating: product.rating,
    short_description: product.short_description,
    image: product.image,
    is_favorite: product.is_favorite,
    video: product.video,
    type: product.type,
    suppliers_prices_enabled: product.suppliers_prices_enabled,
    suppliers: product.suppliers,
    tags: product.tags,
    views: product.views,
    pricing: product.pricing,
    discounts: product.discounts,
    ask_for_price: product.ask_for_price,
  } as any; // Type assertion to bypass strict typing

  const sellable = isSellable(simplifiedProduct, selectedQuantity);

  const generalLayout = useGlobalStore(s => s.generalLayout);
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const isWholesaler = useCustomerStore(s => s.me?.customer.is_wholesaler);
  const currencySymbol = generalLayout.currency;
  const [currencyISO, setCurrencyISO] = useState<string>('EUR');

  const { data: currencies } = useQuery({
    queryKey: [CacheKey.CURRENCIES],
    queryFn: () => fetchCurrencies({ locale }),
  });

  useEffect(() => {
    if (currencies) {
      setCurrencyISO(
        currencies?.data.find(cur => cur.symbol === currencySymbol)?.id || currencySymbol,
      );
    }
  }, [currencies, currencySymbol]);

  async function handleAdd() {
    if (priceDisplay.priceOnRequest) return;
    setIsSubmitting(true);
    try {
      await addToCartMutation.mutateAsync({
        locale,
        body: {
          item: {
            product: { id: variant.product ? variant.product.id : product.id },
            variant: { id: variant.product ? variant.id : 0 },
            sub_items: { packages: [], services: [] },
          },
          qty: selectedQuantity || 1,
          custom_quantity: hasDimensions(product.attributes as any)
            ? getCustomValuesMultiplied(customValues)
            : undefined,
          values: hasDimensions(product.attributes as any)
            ? customValues.map(customValue => ({
                value: { id: customValue.id },
                custom_value: parseFloat(customValue.value.toString()),
              }))
            : undefined,
        },
      });

      if (priceDisplay.currentWithVat != null) {
        trackEcommerce.addToCart({
          id: product.id.toString(),
          name: product.name,
          price: priceDisplay.currentWithVat,
          currency: currencyISO,
        });
      }

      closeDialog(ADD_TO_CART_VARIANT_WITH_VARIANT_DIALOG);
      openDialog({
        id: AddToCartDialog.id,
        content: <AddToCartDialog title="text631" product={simplifiedProduct} />,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV === 'development') console.log(err);
    } finally {
      setIsSubmitting(false);
    }
  }

  const renderErrorMessage = () => {
    if (product.attributes && product.attributes.length > 0) {
      return (
        <span className={cn('text-2xl font-semibold text-destructive-foreground')}>
          {t('text495')}
        </span>
      );
    }
    return (
      <span className={cn('text-2xl font-semibold text-destructive-foreground')}>
        {t('text3132')}
      </span>
    );
  };

  // Initialize dimensions only once per attribute:
  useEffect(() => {
    const initialDimensions: Record<string, string> = {};
    rangeAttributes.forEach(attr => {
      const min = attr.min_range ? parseFloat(attr.min_range.toString()) : 0;
      const max = attr.max_range ? parseFloat(attr.max_range.toString()) : 9999;
      let defaultValue = 1;
      if (defaultValue < min) defaultValue = min;
      if (defaultValue > max) defaultValue = max;

      // Look for an existing custom value in the store for this attribute.
      const existing = customValues.find(
        cv => cv.id === (attr.values && attr.values[0] ? attr.values[0].id : null),
      );
      if (existing && existing.value !== undefined && existing.value !== null) {
        initialDimensions[attr.id] = existing.value.toString();
      } else {
        initialDimensions[attr.id] = defaultValue.toString();
        setCustomValues(prev => [
          ...prev,
          {
            id: attr.values?.[0].id as number,
            name: attr.name,
            unit: attr.values?.[0].name || 'm',
            value: defaultValue,
          },
        ]);
      }
    });
    setDimensions(initialDimensions);
  }, [rangeAttributes, product]);

  // Allow the user to enter a value (even out of range), and update the store.
  const handleDimensionChange = useCallback(
    (id: string, newValue: string) => {
      setDimensions(prev => {
        const newDimensions = { ...prev, [id]: newValue };
        const attribute = rangeAttributes.find(attr => attr.id.toString() === id.toString());
        if (attribute) {
          setCustomValues(prev => {
            const existing = prev.find(cv => cv.id === attribute.values?.[0].id);
            const parsedValue = isNaN(parseFloat(newValue)) ? 1 : parseFloat(newValue);
            if (existing) {
              return prev.map(cv =>
                cv.id === attribute.values?.[0].id ? { ...cv, value: parsedValue } : cv,
              );
            } else {
              return [
                ...prev,
                {
                  id: attribute.values?.[0].id as number,
                  name: attribute.name,
                  unit: attribute.values?.[0].name || 'm',
                  value: parsedValue,
                },
              ];
            }
          });
        }
        return newDimensions;
      });
    },
    [rangeAttributes],
  );

  // Validate each dimension input – show error if out of range.
  useEffect(() => {
    const errors: string[] = [];
    rangeAttributes.forEach(attr => {
      const min = attr.min_range ? parseFloat(attr.min_range.toString()) : 0;
      const max = attr.max_range ? parseFloat(attr.max_range.toString()) : 9999;
      const currentValue = dimensions[attr.id];
      const parsed = parseFloat(currentValue);
      if (currentValue !== '' && !isNaN(parsed) && (parsed < min || parsed > max)) {
        errors.push(`${attr.name}: ${t('text1994')} ${min} - ${max}`);
      }
    });
    setCustomValuesError(errors.length > 0);
  }, [dimensions, rangeAttributes, t]);

  // In the price multiplication, always use a clamped value.
  const multiplier = rangeAttributes.reduce((acc, attr) => {
    const min = attr.min_range ? parseFloat(attr.min_range.toString()) : 0;
    const max = attr.max_range ? parseFloat(attr.max_range.toString()) : 9999;
    const raw = parseFloat(dimensions[attr.id]);
    const value = isNaN(raw) ? 1 : clamp(raw, min, max);
    return acc * value;
  }, 1);

  const priceDisplay = getProductPriceDisplay(product, {
    isWholesaler,
    priceWithoutVatFirst: templateSettings.product_detail?.price_without_vat_first?.enabled,
  });
  const adjustedServicesPrice = roundPrice(
    getServicesPrice(!templateSettings.product_detail?.price_without_vat_first?.enabled),
  );
  const currency = generalLayout.currency;
  const currencySettings = generalLayout.currency_settings;
  const unit = product.unit || 'ks';
  const decimalPlaces = 0;

  return (
    <DialogContent className={cn('flex max-h-[100vh] flex-col gap-4', 'ea-add-to-cart-dialog')}>
      <DialogHeader className={cn('ea-add-to-cart-dialog__header')}>
        <DialogTitle className={cn('text-xl font-semibold', 'ea-add-to-cart-dialog__header-title')}>
          {t('text227')}
        </DialogTitle>
      </DialogHeader>

      <Separator className={cn('ea-add-to-cart-dialog__separator')} />

      <div
        className={cn(
          'hide-scroll-bar flex max-h-[75vh] flex-col items-center gap-4 overflow-auto p-[1px] md:flex-row md:items-start',
          'ea-add-to-cart-dialog__product',
        )}
      >
        <div className={cn('h-20 w-20', 'ea-add-to-cart-dialog__product-image')}>
          {product.image && (
            <Image
              alt={resolveImageAlt(product?.image, product?.name)}
              src={product.image.url}
              width="0"
              height="0"
              sizes="(max-width: 100px) 100vw, 100px"
              className={cn('h-full max-h-[100px] w-full max-w-[100px] object-contain')}
            />
          )}
        </div>

        <div
          className={cn(
            'flex h-full w-full flex-col justify-between gap-4 font-bold',
            'ea-add-to-cart-dialog__product-info',
          )}
        >
          <span className={cn('text-lg font-bold', 'ea-add-to-cart-dialog__product-name')}>
            {product.name}
          </span>

          {/* --- Dimension Inputs (with error messages) --- */}
          {!!hasDimensions(product.attributes as any) && (
            <div className="ea-add-to-cart-dialog__dimensions-inputs flex max-w-[240px] flex-col gap-2">
              {rangeAttributes.map(attr => {
                const min = attr.min_range ? parseFloat(attr.min_range.toString()) : 0;
                const max = attr.max_range ? parseFloat(attr.max_range.toString()) : 9999;
                const currentValue = dimensions[attr.id];
                const parsed = parseFloat(currentValue);
                const error =
                  currentValue !== '' && !isNaN(parsed) && (parsed < min || parsed > max)
                    ? `${t('text1994')} ${min} - ${max}`
                    : null;
                const attrUnit =
                  attr.values && attr.values[0] && attr.values[0].name ? attr.values[0].name : 'm';
                return (
                  <div
                    key={attr.id}
                    className="ea-add-to-cart-dialog__dimension-input flex flex-col"
                  >
                    <label
                      htmlFor={`dimension-${attr.id}`}
                      className={cn('mb-1 text-xs', error && 'text-destructive-foreground')}
                    >
                      <strong>{attr.name}</strong>
                    </label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        id={`dimension-${attr.id}`}
                        value={currentValue}
                        onChange={e => handleDimensionChange(attr.id.toString(), e.target.value)}
                        className={{
                          main: cn(
                            'max-h-10',
                            error
                              ? 'text-destructive-foreground outline outline-[2px] outline-destructive-foreground'
                              : '',
                          ),
                        }}
                      />
                      <span
                        className={cn('text-sm font-bold', error && 'text-destructive-foreground')}
                      >
                        {attrUnit}
                      </span>
                    </div>
                    <div className="h-4">
                      {error && (
                        <span className="text-[12px] font-semibold text-destructive-foreground">
                          {error}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Quantity selector */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedQuantity(Math.max(1, selectedQuantity - 1))}
                disabled={selectedQuantity <= 1}
              >
                -
              </Button>
              <Input
                value={selectedQuantity}
                onChange={e => setSelectedQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className={{
                  wrapper: 'flex w-20 justify-center text-center',
                  main: 'text-center',
                }}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedQuantity(selectedQuantity + 1)}
              >
                +
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            {priceDisplay.priceOnRequest ? (
              <span className="font-semibold text-primary">{t('text2278')}</span>
            ) : priceDisplay.current != null ? (
              <>
                {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null && (
                  <span className="text-sm font-normal text-muted-foreground">
                    {`${t('text1926')} ${formatPrice(
                      priceDisplay.mocPrice,
                      currency,
                      currencySettings,
                      true,
                      multiplier,
                      adjustedServicesPrice,
                    )}${unit}`}
                  </span>
                )}
                <span className={cn('text-lg font-bold', 'ea-add-to-cart-dialog__product-price')}>
                  {`${selectedQuantity.toFixed(decimalPlaces)} x ${formatPrice(
                    priceDisplay.current,
                    currency,
                    currencySettings,
                    true,
                    multiplier,
                    adjustedServicesPrice,
                  )}${unit}`}
                </span>
                {priceDisplay.secondary != null && priceDisplay.showMocVocPrices && (
                  <span className="text-sm font-normal text-muted-foreground">
                    {`${t('text1925')} ${formatPrice(
                      priceDisplay.secondary,
                      currency,
                      currencySettings,
                      true,
                      multiplier,
                      0,
                    )}${unit} ${priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}`}
                  </span>
                )}
              </>
            ) : (
              renderErrorMessage()
            )}
          </div>
        </div>
      </div>

      <Separator className={cn('ea-add-to-cart-dialog__separator')} />

      <DialogFooter
        className={cn(
          'flex flex-row flex-wrap items-center justify-end gap-2',
          'ea-add-to-cart-dialog__footer',
        )}
      >
        <Button
          {...PRODUCT_CONFIG.addToCartDialog.continueShoppingButton}
          onClick={() => closeDialog(ADD_TO_CART_VARIANT_WITH_VARIANT_DIALOG)}
          className={cn(
            'w-full border border-border bg-background text-foreground hover:bg-[hsl(var(--accent)/0.5)] md:w-auto md:flex-1',
            'ea-add-to-cart-dialog__footer-button',
            PRODUCT_CONFIG.addToCartDialog.continueShoppingButton.className,
          )}
          disabled={isSubmitting}
        >
          {t('text208')}
        </Button>
        <Button
          {...PRODUCT_CONFIG.addToCartDialog.addToCartButton}
          className={cn(
            'w-full md:w-auto md:flex-1',
            'ea-add-to-cart-dialog__footer-button',
            PRODUCT_CONFIG.addToCartDialog.addToCartButton.className,
          )}
          disabled={!sellable || customValuesError || priceDisplay.priceOnRequest}
          isLoading={isSubmitting || addToCartMutation.isPending}
          onClick={handleAdd}
        >
          <FiShoppingBag className="mr-2 h-5 w-5" />
          {t('text227')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

AddToCartVariantWithVariantDialog.id = ADD_TO_CART_VARIANT_WITH_VARIANT_DIALOG;
